// AE Home — built to the canonical design (design repo: docs/designs/2026-07-29-ae-homepage/,
// Claude Design "AE View: Landing Page ReDesign"). The landing page owns the ACTIONABLE surface:
// a blockers-first "Waiting on you" list and the led-preprint table. Totals, trends and the
// students roster live on My Productivity, which is historical/reporting only — so the KPI cards,
// the My Preprints columns and the roster this page used to carry are gone, not duplicated.
//
// Central/Admin can still open any AE here (StaffDashboard passes `forcedEditor`). The free-text
// "View as AE" picker is Central/Admin-only when this view is shown without a forced editor.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  MINI_TEAM_MAX,
  NEXT_RANK_ACTIVATE_GREENLIT,
  ON_HOLD_MIN_PROPOSED,
  ON_HOLD_UNCHECKED_RATIO,
  preprintStatusLabel,
  type AeDashboard,
  type DashboardPreprintCard,
} from "@rrid/shared-types";
import { api, ApiError } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { Badge, Button, Card, CardBody, CardHeader, Input, Spinner, statusTone } from "../../components/ui.js";
import { confirmAdvance } from "../preprints/advance-confirms.js";
import { TEAM_CALENDAR_OPEN, TeamCalendarIframe } from "./TeamCalendar.js";

const PUBLIC_SITE_HREF = "https://rrid.mitpress.mit.edu";

/** Statuses the "Current preprints I am leading" table covers, in the design's order. */
const LEADING_ORDER = ["ACTIVE", "ON HOLD", "DORMANT", "To Pitch(Editorial)"];

/** WO-7/30 Track A: the to-do bar's type filter. Buckets mirror the row groups below. */
type TodoKind = "all" | "qc" | "joins" | "pitches";
const TODO_KINDS: { key: TodoKind; label: string }[] = [
  { key: "all", label: "All" },
  { key: "qc", label: "Quality check" },
  { key: "joins", label: "Join requests" },
  { key: "pitches", label: "To editorial" },
];

const uncheckedRatio = (c: DashboardPreprintCard): number => {
  const proposed = c.proposedCount ?? 0;
  return proposed >= ON_HOLD_MIN_PROPOSED ? (c.uncheckedCount ?? 0) / proposed : 0;
};

export function AeDashboardView({
  /** When set (Central/Admin drill-down), lock the view to this AE and hide the free-text picker. */
  forcedEditor,
}: {
  forcedEditor?: string;
} = {}): JSX.Element {
  const { user } = useAuth();
  // Free-text "View as AE" is Central/Admin only. Managers are AEs looking at their own home;
  // Central/Admin normally drill in via StaffDashboard (`forcedEditor`), which hides this picker.
  const roles = user?.roles ?? [];
  const canViewOther =
    (roles.includes("Central") || roles.includes("Admin")) && !forcedEditor;
  const [editorInput, setEditorInput] = useState("");
  const [editor, setEditor] = useState<string | undefined>(undefined);
  const activeEditor = forcedEditor ?? editor;

  const { data, isLoading } = useQuery({
    queryKey: qk.dashboardAe(activeEditor),
    queryFn: () =>
      api.get<AeDashboard>(`/dashboard/ae${activeEditor ? `?editor=${encodeURIComponent(activeEditor)}` : ""}`),
  });

  const firstName = (data?.editor.name ?? user?.name)?.split(" ")[0] || "there";

  if (isLoading || !data) {
    return (
      <div className="space-y-5">
        <h1 className="text-2xl font-bold">Good day, {firstName}</h1>
        <Spinner label="Loading dashboard…" />
      </div>
    );
  }

  const pipeline = data.pipeline ?? [];
  // Everything the AE currently leads that is still in play. INVITED/COMPLETE/DISCARDED are
  // finished with reviewer selection and belong on Productivity, not the actionable landing page.
  const leading = [...data.preprints.active, ...pipeline.filter((p) => p.status === "ON HOLD" || p.status === "DORMANT"), ...data.preprints.toPitchEditorial].sort(
    (a, b) => LEADING_ORDER.indexOf(a.status) - LEADING_ORDER.indexOf(b.status),
  );
  const uncheckedTotal = [...data.preprints.active, ...pipeline].reduce((n, c) => n + (c.uncheckedCount ?? 0), 0);

  const onHoldBlockers = pipeline.filter((p) => p.status === "ON HOLD");
  const joinRequests = data.attention?.joinRequests ?? [];
  const fixed = data.attention?.fixed ?? 0;
  const fixable = data.attention?.fixable ?? 0;
  const blockerCount =
    (fixed > 0 ? 1 : 0) + onHoldBlockers.length + (joinRequests.length > 0 ? 1 : 0) + data.preprints.toPitchEditorial.length;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {data.viewingOther ? `${data.editor.name ?? data.editor.email}'s dashboard` : `Good day, ${firstName}`}
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {blockerCount === 0 ? "Nothing needs you right now." : `${blockerCount} thing${blockerCount === 1 ? "" : "s"} need${blockerCount === 1 ? "s" : ""} you today.`}{" "}
            Totals and trends live on{" "}
            <Link to="/my-productivity" className="font-medium text-brand hover:underline">
              Productivity
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {uncheckedTotal > 0 && <Badge tone="amber">{uncheckedTotal} proposals unchecked</Badge>}
          <Badge tone="green">{data.kpis.greenlitsThisWeek} greenlits this week</Badge>
          <span className="text-xs text-slate-400">Updated just now</span>
        </div>
      </header>

      {canViewOther && (
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setEditor(editorInput.trim() || undefined);
          }}
        >
          <label className="text-xs text-slate-500">
            View as AE
            <Input
              type="email"
              placeholder="ae@berkeley.edu"
              value={editorInput}
              onChange={(e) => setEditorInput(e.target.value)}
              className="mt-0.5 w-56"
            />
          </label>
          <Button type="submit" variant="secondary">
            Open
          </Button>
          {editor && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditor(undefined);
                setEditorInput("");
              }}
            >
              My dashboard
            </Button>
          )}
        </form>
      )}

      <SectionRule title="Waiting on you" note="Blockers first" />
      <WaitingOnYou
        fixed={fixed}
        fixable={fixable}
        onHold={onHoldBlockers}
        joinRequests={joinRequests}
        toPitchEditorial={data.preprints.toPitchEditorial}
      />

      <SectionRule title="Current preprints I am leading" note={`${leading.length} assigned to me`} />
      <LeadingTable cards={leading} />
      <p className="text-[11px] leading-relaxed text-slate-400">
        Proposed counts exclude Student Reject and COI Detected — those never reached quality check.
        {data.deltaSince && (
          <>
            {" "}
            <span className="font-semibold text-green-700">+N</span> is what arrived since your last login on{" "}
            {new Date(data.deltaSince).toLocaleDateString()}.
          </>
        )}
      </p>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Team calendar</h3>
          <a href={TEAM_CALENDAR_OPEN} target="_blank" rel="noopener" className="text-xs font-medium text-brand hover:underline">
            Open in Google Calendar ↗
          </a>
        </CardHeader>
        <TeamCalendarIframe heightClass="h-[520px]" />
      </Card>

      <div className="flex justify-end">
        <a href={PUBLIC_SITE_HREF} target="_blank" rel="noopener" className="text-xs font-medium text-brand hover:underline">
          See the public RR\ID site ↗
        </a>
      </div>

      {data.viewingOther && (
        <p className="text-xs text-slate-400">
          <Badge tone="amber">Viewing</Badge> You are viewing {data.editor.email}. Actions still respect your own
          permissions.
        </p>
      )}
    </div>
  );
}

function SectionRule({ title, note }: { title: string; note: string }): JSX.Element {
  return (
    <div className="flex items-baseline gap-2.5">
      <h2 className="shrink-0 text-xs font-bold uppercase tracking-widest text-slate-600">{title}</h2>
      <span className="h-px flex-1 bg-slate-200" />
      <span className="shrink-0 text-xs text-slate-400">{note}</span>
    </div>
  );
}

// ── Waiting on you ───────────────────────────────────────────────────────────
function BlockerRow({
  chip,
  chipTone,
  title,
  sub,
  children,
}: {
  chip: string;
  chipTone: Parameters<typeof Badge>[0]["tone"];
  title: string;
  sub: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={chipTone}>{chip}</Badge>
          <span className="text-[13px] font-semibold leading-snug text-slate-800">{title}</span>
        </div>
        <div className="text-[11px] text-slate-400">{sub}</div>
      </div>
      {children && <div className="flex shrink-0 flex-wrap justify-end gap-1.5">{children}</div>}
    </div>
  );
}

const PRIMARY_BTN =
  "rounded-md border border-brand bg-brand px-2.5 py-1 text-[11px] font-medium text-white hover:brightness-110";
const QUIET_BTN =
  "rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50";

function WaitingOnYou({
  fixed,
  fixable,
  onHold,
  joinRequests,
  toPitchEditorial,
}: {
  fixed: number;
  fixable: number;
  onHold: DashboardPreprintCard[];
  joinRequests: NonNullable<AeDashboard["attention"]>["joinRequests"];
  toPitchEditorial: DashboardPreprintCard[];
}): JSX.Element {
  // WO-7/30 Track A (Hildy, chat 00:23:30): filter the to-dos by type, so an AE can work one kind
  // at a time — "show only QC items, or only To-Pitch-Editorial→Selected reminders".
  const [kind, setKind] = useState<TodoKind>("all");
  const counts: Record<Exclude<TodoKind, "all">, number> = {
    qc: (fixed > 0 ? 1 : 0) + onHold.length,
    joins: joinRequests.length > 0 ? 1 : 0,
    pitches: toPitchEditorial.length,
  };
  const total = counts.qc + counts.joins + counts.pitches;
  const show = (k: Exclude<TodoKind, "all">): boolean => kind === "all" || kind === k;
  const empty = total === 0;
  const emptyForFilter = kind !== "all" && counts[kind] === 0;

  return (
    <Card>
      {empty ? (
        <CardBody className="text-sm text-slate-500">
          Nothing is blocked on you. {fixable > 0 && `${fixable} proposal${fixable === 1 ? " is" : "s are"} still with students.`}
        </CardBody>
      ) : (
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2.5">
            {TODO_KINDS.map((k) => {
              const n = k.key === "all" ? total : counts[k.key];
              return (
                <button
                  key={k.key}
                  type="button"
                  aria-pressed={kind === k.key}
                  onClick={() => setKind(k.key)}
                  className={clsx(
                    "rounded-full border px-2.5 py-1 text-[11px] font-medium",
                    kind === k.key
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  )}
                >
                  {k.label} <span className="tabular-nums opacity-70">{n}</span>
                </button>
              );
            })}
          </div>

          {emptyForFilter && (
            <CardBody className="text-sm text-slate-500">Nothing of that type is waiting on you.</CardBody>
          )}

          {/* Fixed re-checks first — a student has already replied and is now waiting on the AE. */}
          {show("qc") && fixed > 0 && (
            <BlockerRow
              chip="Fixed"
              chipTone="green"
              title={`${fixed} proposal${fixed === 1 ? "" : "s"} marked Fixed ${fixed === 1 ? "is" : "are"} waiting on your re-check`}
              sub={`${fixable} sent back as Fixable · ${fixed} ${fixed === 1 ? "student has" : "students have"} replied and marked them Fixed`}
            >
              <Link to="/quality-check" className={PRIMARY_BTN}>
                Re-check now
              </Link>
            </BlockerRow>
          )}

          {/* Auto-held preprints: off the propose list until the AE's QC backlog drains. */}
          {show("qc") && onHold.map((c) => {
            const proposed = c.proposedCount ?? 0;
            const unchecked = c.uncheckedCount ?? 0;
            const pct = proposed > 0 ? Math.round((unchecked / proposed) * 100) : 0;
            return (
              <BlockerRow
                key={c.preprintId ?? c.title}
                chip="On Hold"
                chipTone="amber"
                title={c.title}
                sub={`${unchecked} of ${proposed} proposals unchecked (${pct}%) · it returns to Active once the ratio drops under ${Math.round(
                  ON_HOLD_UNCHECKED_RATIO * 100,
                )}%`}
              >
                <Link to="/quality-check" className={PRIMARY_BTN}>
                  Quality check
                </Link>
              </BlockerRow>
            );
          })}

          {show("joins") && joinRequests.length > 0 && <JoinRequests requests={joinRequests} />}

          {/* Pitched at editorial and waiting on the AE to move them on. */}
          {show("pitches") && toPitchEditorial.map((c) => (
            <BlockerRow
              key={c.selectedId ?? c.title}
              chip="To Pitch(Editorial)"
              chipTone="slate"
              title={c.title}
              sub={[c.domain, c.publishedDate ? `published ${c.publishedDate.slice(0, 10)}` : null].filter(Boolean).join(" · ") || "Queued for editorial"}
            >
              {c.selectedId && (
                <>
                  <Link to={`/preprints/${c.selectedId}`} className={QUIET_BTN}>
                    Details
                  </Link>
                  <MoveToSelected selectedId={c.selectedId} />
                </>
              )}
            </BlockerRow>
          ))}
        </div>
      )}
    </Card>
  );
}

/** The design collapses join requests into one row; the one-click Accept shipped in WO-7/22
 *  Track I stays, behind a disclosure so the blockers list keeps its single-row rhythm. */
function JoinRequests({
  requests,
}: {
  requests: NonNullable<AeDashboard["attention"]>["joinRequests"];
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const accept = useMutation({
    mutationFn: ({ preprintId, email }: { preprintId: string; email: string }) =>
      api.post(`/preprints/${preprintId}/accept-join`, { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
  });
  const titles = [...new Set(requests.map((r) => r.preprintTitle))];
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <BlockerRow
        chip="Join requests"
        chipTone="slate"
        title={`${requests.length} student${requests.length === 1 ? "" : "s"} asked to join mini teams you lead`}
        sub={titles.join(" · ")}
      >
        <button type="button" className={PRIMARY_BTN} onClick={() => setOpen((o) => !o)}>
          {open ? "Hide requests" : "Review requests"}
        </button>
      </BlockerRow>
      {open && (
        <div className="space-y-1.5 bg-slate-50 px-4 py-3">
          {accept.isError && (
            <p className="text-xs text-red-700">
              {accept.error instanceof ApiError ? accept.error.message : "Could not accept that request."}
            </p>
          )}
          {requests.map((r) => (
            <div key={`${r.preprintId}:${r.email}`} className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 text-xs text-slate-600">
                <strong className="text-slate-800">{r.name ?? r.email}</strong> → {r.preprintTitle}
              </span>
              <button
                type="button"
                className={QUIET_BTN}
                disabled={accept.isPending}
                onClick={() => accept.mutate({ preprintId: r.preprintId, email: r.email })}
              >
                Accept → add to mini-team
              </button>
            </div>
          ))}
          <p className="text-[11px] text-slate-400">Mini teams cap at {MINI_TEAM_MAX} members.</p>
        </div>
      )}
    </div>
  );
}

/** FR-J5: the "changes discussed in editorial?" confirm must fire on EVERY control that performs
 *  this advance — this is the third such surface, so it reads the shared map like the other two.
 *  The refine/finalize gates stay server-side (409), surfaced here rather than pre-disabled. */
function MoveToSelected({ selectedId }: { selectedId: string }): JSX.Element {
  const qc = useQueryClient();
  const advance = useMutation({
    mutationFn: () => api.post(`/preprints/selected/${selectedId}/advance`, { status: "Selected" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["selected"] });
    },
  });
  return (
    <span className="flex flex-col items-end gap-1">
      <button
        type="button"
        className={PRIMARY_BTN}
        disabled={advance.isPending}
        onClick={() => {
          if (!confirmAdvance("Selected")) return;
          advance.mutate();
        }}
      >
        Move to Selected
      </button>
      {advance.isError && (
        <span className="max-w-56 text-right text-[11px] text-red-700">
          {advance.error instanceof ApiError ? advance.error.message : "Could not move this preprint."}
        </span>
      )}
    </span>
  );
}

// ── Current preprints I am leading ───────────────────────────────────────────
function LeadingTable({ cards }: { cards: DashboardPreprintCard[] }): JSX.Element {
  if (cards.length === 0) {
    return (
      <Card>
        <CardBody className="text-sm text-slate-500">You aren&apos;t leading any preprints right now.</CardBody>
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] border-collapse text-[13px]">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Preprint</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5 text-right">Proposed</th>
              <th className="px-3 py-2.5 text-right">Greenlit</th>
              <th className="px-3 py-2.5 text-right">Unchecked</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c) => (
              <LeadingRow key={c.preprintId ?? c.selectedId ?? c.title} card={c} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LeadingRow({ card }: { card: DashboardPreprintCard }): JSX.Element {
  // To Pitch(Editorial) rows have no reviewer proposals yet — the design renders their counts as
  // em-dashes rather than a misleading zero.
  const preProposal = card.status === "To Pitch(Editorial)";
  const ratio = uncheckedRatio(card);
  const atRisk = !preProposal && ratio >= ON_HOLD_UNCHECKED_RATIO;
  const href = card.selectedId ? `/preprints/${card.selectedId}` : null;
  // WO-7/30 Track A: "20 (+7)" — what arrived since the AE's previous login. Absent (not zero)
  // when there is no baseline yet, and suppressed at 0 so the column stays scannable.
  const num = (n: number | undefined, since?: number, className?: string): JSX.Element => (
    <td className={clsx("px-3 py-3 text-right text-[13px] tabular-nums text-slate-700", className)}>
      {preProposal ? (
        "—"
      ) : (
        <>
          {n ?? 0}
          {(since ?? 0) > 0 && (
            <span className="ml-1 font-semibold text-green-700" title="Since your last login">
              +{since}
            </span>
          )}
        </>
      )}
    </td>
  );
  return (
    <tr className="border-b border-slate-100 align-top last:border-b-0 hover:bg-slate-50">
      <td className="max-w-md px-4 py-3">
        {href ? (
          <Link to={href} className="block text-[13px] font-semibold leading-snug text-slate-800 hover:text-brand">
            {card.title}
          </Link>
        ) : (
          <span className="block text-[13px] font-semibold leading-snug text-slate-800">{card.title}</span>
        )}
        <span className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
          {[card.domain, card.publishedDate ? `published ${card.publishedDate.slice(0, 10)}` : null]
            .filter(Boolean)
            .join(" · ")}
          {(card.miniTeamCount ?? 0) > 0 && (
            <Badge tone="blue">
              Mini team {card.miniTeamCount} of {MINI_TEAM_MAX}
            </Badge>
          )}
          {atRisk && <Badge tone="amber">Auto-hold risk · {Math.round(ratio * 100)}% unchecked</Badge>}
          {!preProposal && (card.greenlitCount ?? 0) >= NEXT_RANK_ACTIVATE_GREENLIT && (
            <Badge tone="green">Next rank activates</Badge>
          )}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-3">
        <Badge tone={statusTone(card.status)}>{preprintStatusLabel(card.status)}</Badge>
      </td>
      {num(card.proposedCount, card.proposedSince)}
      {num(card.greenlitCount, undefined, "font-semibold text-green-700")}
      {num(card.uncheckedCount, card.uncheckedSince, atRisk ? "font-semibold text-amber-700" : undefined)}
      <td className="px-4 py-3">
        <div className="flex flex-wrap justify-end gap-1.5">
          {preProposal ? (
            card.selectedId && (
              <>
                <Link to={`/preprints/${card.selectedId}`} className={QUIET_BTN}>
                  Details
                </Link>
                <MoveToSelected selectedId={card.selectedId} />
              </>
            )
          ) : (
            <>
              <Link to="/quality-check" className={QUIET_BTN}>
                Quality check
              </Link>
              {card.preprintId && (
                <Link to={`/reviewers?preprint=${card.preprintId}`} className={QUIET_BTN}>
                  Find reviewers
                </Link>
              )}
              {/* Mini-team members are added from Find Reviewers, where the roster editor lives. */}
              {card.status === "DORMANT" && card.preprintId && (
                <Link to={`/reviewers?preprint=${card.preprintId}`} className={QUIET_BTN}>
                  Add Miniteam Members
                </Link>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
